import React, { useEffect, useState } from "react";
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Headphones,
  Layers3,
  MoveRight,
  ShieldCheck,
  Sparkles,
  Truck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { getImageUrl } from "../core/api.jsx";
import {
  getBanners,
  getCategories,
  getHomeFeed,
} from "../core/services/catalogService";
import ProductCard from "../shared/ProductCard";
import storeLogo from "../assets/images/logo.png";

function SectionHeading({
  eyebrow,
  title,
  link = "/shop",
  linkLabel = "Explore all",
}) {
  return (
    <div className="section-heading">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
      </div>
      <Link className="text-link" to={link}>
        {linkLabel}
        <ArrowUpRight size={17} />
      </Link>
    </div>
  );
}

function ProductShelf({ title, eyebrow, items = [], loading = false }) {
  if (!loading && !items.length) return null;
  return (
    <section className="section-block page-shell">
      <SectionHeading eyebrow={eyebrow} title={title} />
      {loading ? (
        <div className="product-grid">
          {[0, 1, 2, 3].map((item) => (
            <div className="skeleton-card" key={item}>
              <div />
              <span />
              <span />
            </div>
          ))}
        </div>
      ) : (
        <div className="product-grid">
          {items.slice(0, 4).map((product) => (
            <ProductCard key={product._id} product={product} />
          ))}
        </div>
      )}
    </section>
  );
}

export default function Home() {
  const [feed, setFeed] = useState({
    featuredProducts: [],
    bestSellers: [],
    newArrivals: [],
  });
  const [banners, setBanners] = useState([]);
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    Promise.allSettled([getHomeFeed(), getBanners(), getCategories()]).then(
      (results) => {
        if (!alive) return;
        const [home, bannerResult, categoryResult] = results;
        if (home.status === "fulfilled") setFeed(home.value);
        else
          setError(
            home.reason?.message || "We couldn't load the store just now.",
          );
        if (bannerResult.status === "fulfilled")
          setBanners(bannerResult.value.banners || []);
        if (categoryResult.status === "fulfilled")
          setCategories(categoryResult.value.categories || []);
        setLoading(false);
      },
    );
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (banners.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setActiveBannerIndex((index) => (index + 1) % banners.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [banners.length]);

  const banner = banners[activeBannerIndex] || banners[0];
  const heroImage = getImageUrl(banner?.image);

  const showPreviousBanner = () =>
    setActiveBannerIndex((index) => (index - 1 + banners.length) % banners.length);
  const showNextBanner = () =>
    setActiveBannerIndex((index) => (index + 1) % banners.length);

  return (
    <main>
      <section className={`hero page-shell${heroImage ? " hero--image-only" : ""}`}>
        {!heroImage && <div className="hero__copy">
          <span className="hero__kicker">
            <Sparkles size={14} /> Curated Everyday Essentials
          </span>
          <h1>
            {banner?.title || (
              <>
                Quality finds for
                <br />
                everyday <em>living.</em>
              </>
            )}
          </h1>
          <p>
            {banner?.description ||
              "Explore our premium handpicked collection. Unmatched quality, verified authentic products, and lightning-fast delivery to your door."}
          </p>
          <div className="hero__actions">
            <Link className="button button--primary" to="/shop">
              Shop the Collection <MoveRight size={18} />
            </Link>
            <a className="hero__quiet-link" href="#our-promise">
              Why Sugam Store <ArrowDownRight size={16} />
            </a>
          </div>
          <div className="hero__proof">
            <div className="avatar-stack">
              <span>★</span>
              <span>✓</span>
              <span>✦</span>
            </div>
            <span><strong>4.9/5 Rating</strong> · Over 10,000+ happy customers</span>
          </div>
        </div>}
        <div
          className={`hero__visual${heroImage ? " hero__visual--image" : ""}`}
        >
          {heroImage ? (
            <img
              className="hero__banner"
              src={heroImage}
              alt="Sugam Store promotional banner"
            />
          ) : (
            <>
              <div className="hero__sun" />
              <div className="hero__blob hero__blob--sage" />
              <div className="hero__blob hero__blob--peach" />
              <div className="hero__art-card">
                <img src={storeLogo} alt="Sugam Store" />
              </div>
              <div className="hero__note">
                <span>VERIFIED STORE</span>
                <strong>
                  100% Genuine
                  <br />Everyday Essentials.
                </strong>
                <CheckCircle2 size={20} />
              </div>
            </>
          )}
          {banners.length > 1 && (
            <div className="hero__slider-controls" aria-label="Banner slider controls">
              <button
                className="hero__slider-arrow"
                type="button"
                onClick={showPreviousBanner}
                aria-label="Previous banner"
              >
                <ChevronLeft size={18} />
              </button>
              <div className="hero__slider-dots" aria-label="Choose a banner">
                {banners.map((item, index) => (
                  <button
                    key={item._id || index}
                    type="button"
                    className={`hero__slider-dot${index === activeBannerIndex ? " is-active" : ""}`}
                    onClick={() => setActiveBannerIndex(index)}
                    aria-label={`Show banner ${index + 1}`}
                    aria-current={index === activeBannerIndex ? "true" : undefined}
                  />
                ))}
              </div>
              <button
                className="hero__slider-arrow"
                type="button"
                onClick={showNextBanner}
                aria-label="Next banner"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
          {!heroImage && <div className="hero__visual-caption">
            <span>PREMIUM STORE</span>
            <span>EXPRESS DISPATCH <i>✦</i></span>
          </div>}
        </div>
      </section>

      <section className="trust-strip page-shell" id="our-promise">
        <div>
          <span className="trust-icon"><Truck size={22} /></span>
          <span>
            <strong>Free & Fast Delivery</strong>
            <small>On all orders above ₹499</small>
          </span>
        </div>
        <div>
          <span className="trust-icon"><ShieldCheck size={22} /></span>
          <span>
            <strong>100% Genuine Products</strong>
            <small>Direct from verified sources</small>
          </span>
        </div>
        <div>
          <span className="trust-icon"><Headphones size={22} /></span>
          <span>
            <strong>Dedicated Support</strong>
            <small>Quick & friendly customer help</small>
          </span>
        </div>
      </section>

      {categories.length > 0 && (
        <section className="category-section page-shell">
          <div className="section-heading section-heading--small">
            <div>
              <p className="eyebrow">A good place to start</p>
              <h2>Shop by category</h2>
            </div>
            <Link className="text-link" to="/categories">
              View all
              <ArrowRight size={16} />
            </Link>
          </div>
          <div className="category-list">
            {categories.slice(0, 6).map((category, index) => (
              <Link
                className="category-card"
                key={category._id}
                to={`/shop?category=${encodeURIComponent(category.name)}`}
              >
                <span className="category-card__image-wrap">
                  <Layers3 className="category-card__image-fallback" size={30} />
                  {category.image && (
                    <img
                      className="category-card__image"
                      src={getImageUrl(category.image)}
                      alt={`${category.name} category`}
                      loading="lazy"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  )}
                  <span className="category-card__number">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </span>
                <span className="category-card__details">
                  <strong>{category.name}</strong>
                  <span>Explore category <ArrowUpRight size={15} /></span>
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {error && (
        <div className="page-shell">
          <div className="inline-alert">
            <span>{error}</span>
            <button
              className="text-button"
              onClick={() => window.location.reload()}
            >
              Try again
            </button>
          </div>
        </div>
      )}
      <ProductShelf
        eyebrow="A few favourites"
        title="Made for everyday moments"
        items={feed.featuredProducts}
        loading={loading}
      />
      <section className="editorial-band page-shell">
        <div className="editorial-band__shape editorial-band__shape--one" />
        <div className="editorial-band__shape editorial-band__shape--two" />
        <div className="editorial-band__content">
          <span className="eyebrow">PREMIUM CURATION</span>
          <h2>
            Unmatched Quality.
            <br />
            <em>Honest Prices.</em>
          </h2>
          <p>
            Every product is handpicked and inspected to ensure the highest standards of durability, comfort, and everyday utility.
          </p>
          <Link className="button button--outline-light" to="/shop">
            Explore All Products <ArrowRight size={17} />
          </Link>
        </div>
        <div className="editorial-band__stamp">
          <span>SU</span>
          <small>
            SUGAM
            <br />
            STORE
          </small>
        </div>
      </section>
      <ProductShelf
        eyebrow="Popular right now"
        title="Trending & Bestsellers"
        items={feed.bestSellers}
        loading={loading}
      />
      <ProductShelf
        eyebrow="Fresh from the shelves"
        title="Newly Added Products"
        items={feed.newArrivals}
        loading={loading}
        linkLabel="See all new arrivals"
      />
      {!loading &&
        !error &&
        !feed.featuredProducts?.length &&
        !feed.bestSellers?.length &&
        !feed.newArrivals?.length && (
          <section className="section-block page-shell empty-catalog">
            <span className="eyebrow">A fresh start</span>
            <h2>New arrivals are on their way.</h2>
            <p>
              We're stocking the shelves with great products. Check back soon or explore the
              full catalog.
            </p>
            <Link className="button button--primary" to="/shop">
              Browse the catalog <ArrowRight size={17} />
            </Link>
          </section>
        )}

      <section className="newsletter-band page-shell">
        <div>
          <span className="eyebrow">EXCLUSIVE OFFERS & UPDATES</span>
          <h2>
            Get ₹100 Off Your
            <br />
            First Order.
          </h2>
          <p>Subscribe for member-only discounts, new launch alerts, and special sale previews.</p>
        </div>
        <Link className="button button--primary" to="/shop">
          Start Shopping Now <ArrowRight size={17} />
        </Link>
      </section>
    </main>
  );
}
